import { useCallback, useEffect, useRef, useState } from "react";
import {
  generateThgCreditNote,
  getThgCreditNotePdfUrl,
  syncThgPayouts,
} from "../../../services/clearing/thg-payouts";
import type {
  ThgPayoutFailure,
  ThgPayoutResult,
  ThgPayoutSyncResult,
  ThgPdfLink,
  ThgStationPayout,
  ThgStationRow,
} from "../../../types/thgAnnualSummary";
import { PAYOUT_COPY } from "../ThgAnnualSummary.consts";
import { payoutFailureNotice, SyncNotice, syncResultNotice } from "../utils/thgPayout";

export type PayoutActionKind = "generate" | "pdf" | "sync";

export interface PayoutBusy {
  kind: PayoutActionKind;
  stationId: string | null;
}

export interface PayoutConfirm {
  kind: "generate";
  row: ThgStationRow;
}

export interface PayoutNotification {
  severity: "success" | "info" | "warn" | "error";
  summary: string;
  detail: string;
}

interface UseThgPayoutActionsArgs {
  year: number;
  /** Re-reads the year without clearing the table; false when it failed. */
  refresh: () => Promise<boolean>;
  patchStationPayout: (stationId: string, payout: ThgStationPayout) => void;
  notify: (notification: PayoutNotification) => void;
}

/**
 * The CLR-283 credit-note actions: generate a draft, open the PDF, and sync. Finalising and sending happen in
 * the accounting system, outside the Back Office.
 *
 * One action at a time for the whole page (research R2). The accounting system takes 10 requests a minute
 * for the whole application, so one page must never contribute a burst, and nothing is retried. The lock is
 * a ref as well as state, so a double click inside one render cannot start two requests. It is held until
 * the table has been reloaded, so no action can be started on a row that is out of date.
 */
export const useThgPayoutActions = ({ year, refresh, patchStationPayout, notify }: UseThgPayoutActionsArgs) => {
  const [busy, setBusy] = useState<PayoutBusy | null>(null);
  const [confirm, setConfirm] = useState<PayoutConfirm | null>(null);
  const [sync, setSync] = useState<{ year: number; notice: SyncNotice } | null>(null);
  const busyRef = useRef(false);

  // A sync result belongs to the year it ran for; switching away and back does not bring it back.
  useEffect(() => setSync(null), [year]);

  const run = useCallback(
    async (next: PayoutBusy, action: () => Promise<void>) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(next);
      try {
        await action();
      } finally {
        busyRef.current = false;
        setBusy(null);
      }
    },
    [],
  );

  const reload = useCallback(async () => {
    if (!(await refresh())) {
      notify({ severity: "warn", summary: "Warning", detail: PAYOUT_COPY.reloadFailed });
    }
  }, [refresh, notify]);

  const reportFailure = useCallback(
    async (failure: ThgPayoutFailure, stationId: string | null) => {
      const { reload: shouldReload, ...notification } = payoutFailureNotice(failure, stationId);
      notify(notification);
      if (shouldReload) await reload();
    },
    [notify, reload],
  );

  const requestGenerate = useCallback((row: ThgStationRow) => {
    if (!busyRef.current) setConfirm({ kind: "generate", row });
  }, []);

  const cancelConfirm = useCallback(() => setConfirm(null), []);

  /** Runs the confirmed action. */
  const accept = useCallback(async () => {
    const pending = confirm;
    setConfirm(null);
    if (!pending) return;
    const { kind, row } = pending;
    const { stationId } = row;

    await run({ kind, stationId }, async () => {
      const result: ThgPayoutResult<ThgStationPayout> = await generateThgCreditNote(year, stationId);
      if (result.success === false) {
        await reportFailure(result, stationId);
        return;
      }
      patchStationPayout(stationId, result.data);
      notify({ severity: "success", summary: "Success", detail: PAYOUT_COPY.generated(stationId) });
      // The response reports no mismatches and no totals, so the full row comes from a reload (research R5).
      await reload();
    });
  }, [confirm, run, year, reportFailure, patchStationPayout, notify, reload]);

  /**
   * Opens the credit note PDF. The tab is opened on the click itself, while the browser still counts it as
   * the reader's gesture, and pointed at the short-lived link once it arrives (research R4).
   */
  const openPdf = useCallback(
    async (row: ThgStationRow) => {
      if (busyRef.current) return;
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      await run({ kind: "pdf", stationId: row.stationId }, async () => {
        const result: ThgPayoutResult<ThgPdfLink> = await getThgCreditNotePdfUrl(year, row.stationId);
        if (result.success === false) {
          tab?.close();
          await reportFailure(result, row.stationId);
          return;
        }
        if (tab) tab.location.href = result.data.url;
        else window.open(result.data.url, "_blank", "noopener,noreferrer");
      });
    },
    [run, year, reportFailure],
  );

  const runSync = useCallback(async () => {
    await run({ kind: "sync", stationId: null }, async () => {
      const result: ThgPayoutResult<ThgPayoutSyncResult> = await syncThgPayouts(year);
      if (result.success === false) {
        await reportFailure(result, null);
        return;
      }
      setSync({ year, notice: syncResultNotice(result.data, year) });
      await reload();
    });
  }, [run, year, reportFailure, reload]);

  return {
    busy,
    confirm,
    requestGenerate,
    cancelConfirm,
    accept,
    openPdf,
    sync: runSync,
    /** The last sync's outcome for the shown year; gone once the reader switches year. */
    syncNotice: sync && sync.year === year ? sync.notice : null,
  };
};
